/**
 * Main entry point — Safety Rail Source
 * Initializes all section modules.
 */

document.addEventListener('DOMContentLoaded', function () {
  // Global utilities (always loaded via utils.js + this file)
  if (typeof initScrollReveal       === 'function') initScrollReveal();
  if (typeof initLazyLoad           === 'function') initLazyLoad();
  if (typeof initEPrefixCasing      === 'function') initEPrefixCasing();
  if (typeof initSearchResultsPage  === 'function') initSearchResultsPage();
  initRichTextTrim();
  initTrademarkSupClasses();
  initVimotiaAltFix();

  // Section behaviors — each function lives in its own section JS file
  // (e.g. initAccordions → section-services.js, initCarousel →
  // section-reviews.js, initNewsletterForm → section-newsletter.js).
  // Those files are only included on pages that render the section, so
  // on other pages the function is undefined. Guard each call so a
  // missing per-section script can't break the rest of the chain.
  if (typeof initAccordions     === 'function') initAccordions();
  if (typeof initCarousel       === 'function') initCarousel();
  if (typeof initNewsletterForm === 'function') initNewsletterForm();
});

/**
 * Rich-text edge trim.
 *
 * The metafield / app-authored copy in these blocks routinely ends with an
 * empty paragraph — <p>&nbsp;</p>, <p><br></p> or similar. Those are real
 * elements with real line boxes, so they render as a dead band at the foot
 * of the section (reported on /pages/skylight-fall-protection) that no
 * amount of padding tuning can explain. CSS can't reach them: :empty does
 * not match a <p> that contains a non-breaking space.
 *
 * So we drop blank leading/trailing blocks at runtime and let the block's
 * own padding provide the spacing instead. Anything carrying real content —
 * text, an image, a table, an <hr> — is left alone, and trimming stops at
 * the first non-blank element on each side, so blank paragraphs used as
 * deliberate mid-copy spacers survive.
 */
function initRichTextTrim() {
  var BLOCKS = '.sc-seo-content, .col-extra-desc__content, .sc-description';
  /* Elements that are meaningful even with no text of their own. */
  var MEANINGFUL = 'img, picture, source, svg, video, iframe, embed, object, table, hr, input, button, canvas';

  function isBlank(el) {
    if (!el || el.nodeType !== 1) return false;
    if (el.nodeName === 'BR' || el.nodeName === 'HR') return el.nodeName === 'BR';
    if (el.matches && el.matches(MEANINGFUL)) return false;
    if (el.querySelector && el.querySelector(MEANINGFUL)) return false;
    /* \u00A0 is &nbsp; — the usual culprit. */
    return (el.textContent || '').replace(/[\s\u00A0]+/g, '') === '';
  }

  function trim(container) {
    if (!container) return;
    var el;
    while ((el = container.lastElementChild) && isBlank(el)) container.removeChild(el);
    while ((el = container.firstElementChild) && isBlank(el)) container.removeChild(el);
  }

  function run(root) {
    if (!root || !root.querySelectorAll) return;
    if (root.matches && root.matches(BLOCKS)) trim(root);
    var els = root.querySelectorAll(BLOCKS);
    for (var i = 0; i < els.length; i++) trim(els[i]);
  }

  run(document);

  /* Theme editor re-renders a section from the server, restoring the blanks. */
  document.addEventListener('shopify:section:load', function (e) {
    run(e.target);
  });
}

/**
 * Trademark / registered superscript normaliser.
 *
 * Two passes, both idempotent:
 *
 *   1. classify()  — any <sup>®</sup> / <sup>™</sup> that arrives without
 *      the rmark / tmark class (admin rich text, third-party widgets,
 *      the Searchanise wrapper in utils.js) gets the right class so the
 *      global rules in base.css size it consistently.
 *
 *   2. wrapBare()  — any BARE ® / ™ character in a text node gets wrapped
 *      in <sup class="rmark|tmark">. This is the part that fixes rich-text
 *      content: metafield copy such as the collection description
 *      (.col-extra-desc__content) and the Super Collection SEO block
 *      (.sc-seo-content) is echoed raw, so the per-section Liquid
 *      `| replace: '®', '<sup class="rmark">®</sup>'` filters never touch
 *      it. ™ still LOOKED right there because the ™ glyph is drawn small
 *      and raised by the font anyway, while ® is a full-size baseline
 *      glyph — which is why only ® was reported as broken.
 *
 * Both marks are wrapped, not just ®: base.css sizes tmark (0.55em) and
 * rmark (0.65em) against each other so the two render optically equal.
 * Wrapping only ® would leave a shrunken ® next to a full-size ™ in the
 * same sentence. To revert to ®-only, drop \u2122 from MARKS below.
 *
 * Runs over <body> on load and re-runs on injected DOM via MutationObserver.
 */
function initTrademarkSupClasses() {
  var MARKS = /([\u00AE\u2122])/;                 // ® and ™
  var MARK_CLASS = { '\u00AE': 'rmark', '\u2122': 'tmark' };

  /* Never rewrite text inside these — JSON-LD and inline scripts would be
     corrupted, form controls can't hold markup, and SUP means the mark has
     already been handled (this is what keeps the pass idempotent). */
  var SKIP_TAGS = {
    SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEXTAREA: 1, TITLE: 1, HEAD: 1,
    SUP: 1, SELECT: 1, OPTION: 1, CODE: 1, PRE: 1, IFRAME: 1, SVG: 1
  };

  function inSkippedSubtree(node) {
    for (var p = node.parentNode; p && p.nodeType === 1; p = p.parentNode) {
      if (SKIP_TAGS[p.nodeName.toUpperCase()]) return true;
      if (p.isContentEditable) return true;
      if (p.hasAttribute && p.hasAttribute('data-no-trademark')) return true;
    }
    return false;
  }

  function classify(root) {
    if (!root || !root.querySelectorAll) return;
    var sups = root.querySelectorAll('sup:not(.tmark):not(.rmark)');
    for (var i = 0; i < sups.length; i++) {
      var t = (sups[i].textContent || '').trim();
      if (t === '\u00AE') sups[i].classList.add('rmark');       // ®
      else if (t === '\u2122') sups[i].classList.add('tmark');  // ™
    }
  }

  function wrapBare(root) {
    if (!root || root.nodeType !== 1 || !document.createTreeWalker) return;

    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (n) {
        if (!MARKS.test(n.nodeValue)) return NodeFilter.FILTER_REJECT;
        return inSkippedSubtree(n) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
      }
    });

    /* Collect first, mutate after — replacing nodes mid-walk invalidates it. */
    var nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    if (!nodes.length) return;

    for (var i = 0; i < nodes.length; i++) {
      var textNode = nodes[i];
      if (!textNode.parentNode) continue;

      var parts = textNode.nodeValue.split(MARKS);   // capture group keeps the marks
      var frag = document.createDocumentFragment();

      for (var p = 0; p < parts.length; p++) {
        var part = parts[p];
        if (!part) continue;
        if (MARK_CLASS[part]) {
          var sup = document.createElement('sup');
          sup.className = MARK_CLASS[part];
          sup.textContent = part;
          frag.appendChild(sup);
        } else {
          frag.appendChild(document.createTextNode(part));
        }
      }
      textNode.parentNode.replaceChild(frag, textNode);
    }
  }

  function run(root) {
    classify(root);
    wrapBare(root);
  }

  run(document.body);

  /* Live-injected content (apps, Searchanise re-renders, cart drawer).
     Our own <sup> insertions fire this observer too, but their text sits
     inside a SUP, so inSkippedSubtree() rejects it and the loop terminates. */
  if (!('MutationObserver' in window)) return;
  var mo = new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType === 1) {
          run(node);
        } else if (node.nodeType === 3 && node.parentNode && MARKS.test(node.nodeValue)) {
          /* A bare text node dropped straight into an existing element. */
          wrapBare(node.parentNode);
        }
      }
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
}

/**
 * Vimotia video thumbnail alt fix.
 * The Hextom Vimotia app injects its video thumbnail at runtime:
 *   <img class="vimotia__video-wrapper__background" src="...">
 * That element isn't in the theme, so the alt can't be set in Liquid.
 * This watches the DOM and sets a descriptive alt once the app renders
 * the image. Idempotent — re-applies to live-injected content.
 */
function initVimotiaAltFix() {
  var ALT = 'about-us';
  var SELECTOR = 'img.vimotia__video-wrapper__background';

  function applyAlt(root) {
    if (!root || !root.querySelectorAll) return;
    var imgs = root.querySelectorAll(SELECTOR);
    for (var i = 0; i < imgs.length; i++) {
      if (imgs[i].getAttribute('alt') !== ALT) imgs[i].setAttribute('alt', ALT);
    }
  }

  applyAlt(document);
  var mo = new MutationObserver(function () { applyAlt(document); });
  mo.observe(document.body, { childList: true, subtree: true });
}
